import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Screen, Input, Select, Button, Checkbox, Message} from '../components';
import {setUser} from '../actions';
import {__, toNumber, isEmptyString, request, getUrl} from '../functions';
import {COUNTRIES, COURIERS} from '../config';
import '../assets/styles/point.css';
import {IconButton, Tooltip} from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CopyIcon from '@mui/icons-material/ContentCopy';

/**
 * Point.
 */
class PointScreen extends Screen {
    title = __('Mapa');

    state = {
        data: {},
        id: '',
        settings: {},
        loading: false,
        version: 'pc',
    };

    async componentDidMount() {
        if (super.componentDidMount()) {
            const { params, setUser } = this.props;

            request(`/pickup-points/view/${params.id}/${params.user_courier_id}`).then(response => {
                const { status, data, sync } = response.data;

                if (status === 'error') {
                    window.location = '/points';
                    return;
                }

                setUser(sync.user);

                let settings = data.settings;

                if (settings.country === '') {
                    const default_country = data.countries[0];

                    settings = { ...settings, country: default_country, ...this.getCountryDefaults(data.defaults, default_country) };
                }

                if (!_.isEmpty(settings.types)) {
                    settings = { ...settings, types: _.join(settings.types, '===') };
                }

                if (!_.isEmpty(settings.merged_maps)) {
                    settings = { ...settings, merged_maps: _.join(settings.merged_maps, '===') };
                }

                this.setState({
                    id: params.id,
                    data,
                    settings,
                });
            });
        }

        return true;
    }

    getCountryDefaults(defaults, country) {
        if (!_.has(defaults, country)) {
            return {};
        }

        let result = {};

        if (_.has(defaults[country], 'lat')) {
            result = { ...result, default_lat: defaults[country].lat };
        }

        if (_.has(defaults[country], 'lng')) {
            result = { ...result, default_lng: defaults[country].lng };
        }

        if (_.has(defaults[country], 'zoom')) {
            result = { ...result, default_zoom: defaults[country].zoom };
        }

        if (_.has(defaults[country], 'zoom_mobile')) {
            result = { ...result, default_zoom_mobile: defaults[country].zoom_mobile };
        }

        return result;
    }

    save() {
        const { params } = this.props;
        let { id, settings } = this.state;

        if (this.isDemo()) {
            this.showSnackbar('error', __('Mapu nie je možné uložiť na DEMO účte'));
            return;
        }

        let error = '';

        if (isEmptyString(settings.country)) {
            error = __('Nie je vyplnená krajina');
        } else if (isEmptyString(settings.url)) {
            error = __('Nie je vyplnená doména');
        } else if (isEmptyString(settings.delivery_name)) {
            error = __('Nie je vyplnený typ dodania');
        }

        if (error !== '') {
            // Mame error
            this.showSnackbar('error', error);
            return;
        }

        this.setState({ loading: true });

        if (!_.isEmpty(settings.types)) {
            settings = { ...settings, types: _.split(settings.types, '===') };
        }

        if (!_.isEmpty(settings.merged_maps)) {
            settings = { ...settings, merged_maps: _.split(settings.merged_maps, '===') };
        }

        if (toNumber(id) > 0) {
            request(`/pickup-points/edit/${id}`, { settings }, 'POST').then(response => {
                const { status } = response.data;

                this.setState({ loading: false });

                if (status === 'error') {
                    this.showSnackbar('error', __('Nepodarilo sa uložiť mapu'));
                    return;
                }

                this.showSnackbar('success', __('Mapa bola uložená'));

                setTimeout(() => { window.location = `/maps/view/${id}/${params.user_courier_id}` }, 500);
            });

            return;
        }

        request(`/pickup-points/create/${params.user_courier_id}`, { settings }, 'POST').then(response => {
            const { data, status } = response.data;

            this.setState({ loading: false });

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa vytvoriť mapu'));
                return;
            }

            this.showSnackbar('success', __('Mapa bola vytvorená'));

            setTimeout(() => { window.location = `/maps/view/${data.id}/${params.user_courier_id}` }, 500);
        });
    }

    onChangeSetting(type, value) {
        const { data, settings } = this.state;

        let additional = {};

        if (type === 'country') {
            additional = this.getCountryDefaults(data.defaults, value);
        }

        this.setState({ settings: { ...settings, [type]: value, ...additional } });
    }

    changeVersion(version) {
        this.setState({ version });
    }

    copyCode(code) {
        // Nastavime placeholder do clipboardu
        navigator.clipboard.writeText(code);

        this.showSnackbar('success', __('Skopirované do schránky'));
    }

    /**
     * Rendrovanie.
     *
     * @returns {JSX.Element}
     */
    render() {
        const { id, data, loading, settings, version } = this.state;

        if (_.isEmpty(data)) {
            // Data nie su nacitane
            return this.renderLoading();
        }

        const isEdit = toNumber(id) > 0;

        if (isEdit && !this.hasPermission('points-edit')) {
            // Nema pravo
            return null;
        } else if (!isEdit && !this.hasPermission('points-create')) {
            // Nema pravo
            return null;
        }

        const countries = _.reduce(data.countries, (result, country) => ({ ...result, [country]: country }), {});
        const allowed_eshop = data.allowed_eshop;
        const code = allowed_eshop ? `<script src="${getUrl()}/points/${data.token}/main.js?v=${data.version}" defer></script>` : '';

        const maps = _.reduce(data.maps, (result, map) => ({ ...result, [map.token]: `${_.has(COURIERS, map.courier_name) ? COURIERS[map.courier_name] : map.courier_name} - ${_.has(COUNTRIES, map.country) ? COUNTRIES[map.country] : map.country}` }), {});

        return (
            <div className="point">
                <div className="point__header">
                    <div className="point__header__left">
                        <div className="point__header__left__title">
                            {`${COURIERS[data.courier_name]} - ${isEdit ? __('úprava mapy') : __('nová mapa')}`}
                        </div>
                    </div>
                    <div className="point__header__right">
                        <Button
                            onClick={() => window.location = '/maps'}
                            color="shadow"
                        >{__('Naspäť')}</Button>
                    </div>
                </div>
                {!allowed_eshop ? <Message type="warning">{__('Mapu pre váš eshopový systém nepodporujeme, prosím kontaktujte nás.')}</Message> : null}
                <div className="point__content">
                    <div className="point__content__panels">
                        <div className="point__content__panels__panel">
                            <div className="point__content__panels__panel__title">{__('Nastavenia')}</div>
                            <Message type="info">{__('Je možné vytvoriť maximálne jednu mapu v každej krajine daného prepravcu.')}</Message>
                            <Select
                                label={__('Krajina')}
                                value={settings.country}
                                onChange={value => this.onChangeSetting('country', value)}
                                options={countries}
                                allowEmpty={false}
                            />
                            <div className="point__content__panels__panel__info">{__('Krajina výdajných miest.')}</div>
                            <Input
                                label={__('Doména')}
                                value={settings.url}
                                onChange={value => this.onChangeSetting('url', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Doména eshopu (príklad mojeshop.com). Slúži na kontrolu. Zadáva sa bez https:// a www.')}</div>
                            <Input
                                label={__('Typ dodania')}
                                value={settings.delivery_name}
                                onChange={value => this.onChangeSetting('delivery_name', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Názov dodacej metódy v eshope pre ktorú sa má zobrazovať mapa.')}</div>
                            {!_.isEmpty(data.types) ? <Select
                                label={__('Povolené typy výdajných miest')}
                                value={settings.types}
                                onChange={value => this.onChangeSetting('types', value)}
                                options={data.types}
                                multiple
                            /> : null}
                            {!_.isEmpty(data.types) ? <div className="point__content__panels__panel__info">{__('Ak nie je vyplnené, zobrazujú sa všetky.')}</div> : null}
                            <div className="point__content__panels__panel__part" />
                            <Checkbox
                                label={__('Povoliť výdajné miesta s dobierkou')}
                                value={settings.allow_cod}
                                onChange={checked => this.onChangeSetting('allow_cod', checked)}
                            />
                            <Checkbox
                                label={__('Povoliť výdajné miesta s kartou')}
                                value={settings.allow_card}
                                onChange={checked => this.onChangeSetting('allow_card', checked)}
                            />
                            <Input
                                label={__('Predvolená súradnica (latitude)')}
                                value={settings.default_lat}
                                onChange={value => this.onChangeSetting('default_lat', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Súradnica pre úvodné zobrazenie mapy. Predvolené podľa krajiny výdajných miest.')}</div>
                            <Input
                                label={__('Predvolená súradnica (longitude)')}
                                value={settings.default_lng}
                                onChange={value => this.onChangeSetting('default_lng', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Súradnica pre úvodné zobrazenie mapy. Predvolené podľa krajiny výdajných miest.')}</div>
                            <Input
                                label={__('Predvolené priblíženie mapy (desktop)')}
                                value={settings.default_zoom}
                                onChange={value => this.onChangeSetting('default_zoom', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Hodnota priblíženia úvodneho zobrazenia mapy na desktope. Predvolené podľa krajiny výdajných miest.')}</div>
                            <Input
                                label={__('Predvolené priblíženie mapy (mobil)')}
                                value={settings.default_zoom_mobile}
                                onChange={value => this.onChangeSetting('default_zoom_mobile', value)}
                            />
                            <div className="point__content__panels__panel__info">{__('Hodnota priblíženia úvodneho zobrazenia mapy na mobile. Predvolené podľa krajiny výdajných miest.')}</div>
                        </div>
                        <div className="point__content__panels__panel">
                            {data.token !== '' && allowed_eshop && data.shopify_token === '' ? <Message type="warning">{__('Vložte tento kód do zdrojového kódu vašej web stránky, alebo poproste vášho web administrátora, aby to spravil.')}</Message> : null}
                            {data.token !== '' && allowed_eshop && data.shopify_token === '' ? <div className="point__content__panels__panel__code">
                                <Input
                                    value={code}
                                    multiline
                                />
                                <Tooltip title={__('Skopírovať')}>
                                    <IconButton onClick={() => this.copyCode(code)}>
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </div> : null}
                            {data.shopify_token !== '' ? <Message type="warning">{__('Klikom na tlačítko nižšie nainštalujte našu aplikáciu vo vašom Shopify.')}</Message> : null}
                            {data.shopify_host !== '' ? <Button
                                onClick={() => window.open(`https://shopify-maps.goodeshop.io/auth?shop=${data.shopify_host}.myshopify.com`, '_blank')}
                                className="point__content__panels__panel__button"
                                color="primary"
                            >{__('Nainštalovať aplikáciu')}</Button> : null}
                            {data.shopify_token !== '' ? <div className="point__content__panels__panel__code">
                                <Input
                                    label={__('API token pre Shopify aplikáciu')}
                                    value={data.shopify_token}
                                    multiline
                                />
                                <Tooltip title={__('Skopírovať')}>
                                    <IconButton onClick={() => this.copyCode(data.shopify_token)}>
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </div> : null}
                            <Message type="info">{__('Aktuálna mapa sa zobrazí po uložení.')}</Message>
                            {!_.isEmpty(maps) ? <Select
                                label={__('Zlúčiť s mapami')}
                                value={settings.merged_maps}
                                onChange={value => this.onChangeSetting('merged_maps', value)}
                                options={maps}
                                multiple
                            /> : null}
                            {data.token !== '' ? <div className="point__content__panels__panel__buttons">
                                <Tooltip title={__('Desktop verzia')}>
                                    <IconButton onClick={() => this.changeVersion('pc')} className={version === 'pc' ? 'active' : ''}>
                                        <ComputerIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={__('Mobilná verzia')}>
                                    <IconButton onClick={() => this.changeVersion('mobile')} className={version === 'mobile' ? 'active' : ''}>
                                        <PhoneIphoneIcon />
                                    </IconButton>
                                </Tooltip>
                            </div> : null}
                            <div className={`point__content__panels__panel__map ${version}`}>
                                {data.token !== '' ? <iframe src={`https://api.goodeshop.io/map.php?token=${data.token}`} /> : null}
                            </div>
                        </div>
                    </div>
                    <Button
                        onClick={() => this.save()}
                        loading={loading}
                        className="point__content__button"
                        color="green"
                    >{__('Uložiť')}</Button>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ user }) => ({ user });

export default withCookies(connect(stateToProps, {
    setUser,
})(PointScreen));
